/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { Ref } from 'preact/hooks';
import type {
  GanttCurrentRowLabel,
  GanttRowData,
  GanttRowAxisProps as GanttRowAxisObject,
  GanttRowLabelHover,
  GanttTaskData,
  GanttRowAxisLabelContext
} from './gantt.types';
import { getRenderContext } from './utils/rowAxisUtils';
import type { getRowDataLayout } from './utils/dataLayoutUtils';
import { rowAxisStyles } from './themes/GanttStyles.css';
import { classNames } from '../utils/UNSAFE_classNames';
import { GanttRowLabelContainer } from './GanttRowLabelContainer';
import { useGanttRowLabelDatatip } from './hooks/useGanttRowLabelDatatip';
import { useGanttRowLabelPointerGestures } from './hooks/useGanttRowLabelPointerGestures';
import { mergeProps } from '../utils/UNSAFE_mergeProps';

type RowDataLayout<
  K1,
  K2,
  D1 extends GanttRowData<K1, D1, D2>,
  D2 extends GanttTaskData<K2>
> = ReturnType<typeof getRowDataLayout<K1, K2, D1, D2>>;
type GanttRowAxisLabel<D1> = NonNullable<GanttRowAxisObject<D1>['labelRenderer']>;

type Props<K1, K2, D1 extends GanttRowData<K1, D1, D2>, D2 extends GanttTaskData<K2>> = {
  innerRef: Ref<HTMLDivElement>;
  scrollPosition: number;
  resolvedWidth: number;
  resolvedMaxWidth: number;
  currentRowLabel: GanttCurrentRowLabel<K1>['currentRowLabel'];
  currentRowLabelInteraction: 'hover' | 'focus' | 'none';
  layoutObj: {
    labelPaddingInlineStart: number;
    labelPaddingInlineEnd: number;
    rowData: RowDataLayout<K1, K2, D1, D2>['rowData'];
    rowObjs: RowDataLayout<K1, K2, D1, D2>['rowObjs'];
    idRowObjMap: RowDataLayout<K1, K2, D1, D2>['idRowObjMap'];
    isHorizontalGridlinesVisible: boolean;
  };
  ariaActive?: { dataId: K1; ariaId: string; type: 'rowLabel' };
  children: GanttRowAxisLabel<D1>;
} & GanttRowLabelHover<K1>;

export function GanttRowAxis<
  K1,
  K2,
  D1 extends GanttRowData<K1, D1, D2>,
  D2 extends GanttTaskData<K2>
>({
  innerRef,
  scrollPosition,
  resolvedWidth,
  resolvedMaxWidth,
  currentRowLabel,
  currentRowLabelInteraction,
  hoveredRowLabelId,
  onRowLabelHoverChange,
  layoutObj: {
    labelPaddingInlineStart,
    labelPaddingInlineEnd,
    rowData,
    rowObjs,
    idRowObjMap,
    isHorizontalGridlinesVisible
  },
  ariaActive,
  children
}: Props<K1, K2, D1, D2>) {
  const width = resolvedWidth !== Infinity ? resolvedWidth : undefined;
  const maxWidth = resolvedMaxWidth !== Infinity ? resolvedMaxWidth : undefined;
  const availableWidth = Math.min(resolvedWidth, resolvedMaxWidth);
  const contextMaxWidth =
    availableWidth === Infinity
      ? -1
      : availableWidth - (labelPaddingInlineStart + labelPaddingInlineEnd);

  const gestureProps = useGanttRowLabelPointerGestures({
    rowObjs,
    hoverProps: { hoveredRowLabelId, onRowLabelHoverChange }
  });

  const { datatipContent, datatipProps } = useGanttRowLabelDatatip<K1, K2, D1, D2>({
    rowData,
    hoveredRowObj: hoveredRowLabelId && idRowObjMap.get(hoveredRowLabelId),
    focusedRowObj: currentRowLabel?.isFocused ? idRowObjMap.get(currentRowLabel.id) : undefined,
    currentRowLabelInteraction,
    // TODO: Evaluate whether we should get real element bounds by measuring.
    // It's unfortunate that we need to measure JUST for keyboard datatip positioning...
    rowAxisRegion: {
      x: 0,
      y: -scrollPosition,
      w: (labelPaddingInlineStart + labelPaddingInlineEnd) * 2,
      h: -1
    },
    targetRef: innerRef
  });

  return (
    <div
      ref={innerRef}
      {...mergeProps(gestureProps, datatipProps)}
      class={classNames([
        rowAxisStyles.base,
        isHorizontalGridlinesVisible && rowAxisStyles.withSeparatorTop
      ])}
      style={{ width, maxWidth }}>
      <div role="grid" class={rowAxisStyles.contentContainer} style={{ top: -scrollPosition }}>
        {rowObjs.map((rowObj) => {
          return (
            <GanttRowLabelContainer
              ariaActive={ariaActive}
              rowObj={rowObj}
              rowData={rowData}
              isRowSelected={rowObj.isSelected}
              isFocused={!!currentRowLabel?.isFocused && currentRowLabel.id === rowObj.id}
              isHorizontalGridlinesVisible={isHorizontalGridlinesVisible}>
              {children(
                getRenderContext(rowObj, rowData, contextMaxWidth) as GanttRowAxisLabelContext<D1>
              )}
            </GanttRowLabelContainer>
          );
        })}
      </div>
      {datatipContent}
    </div>
  );
}
